import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import useModalContext from 'components/context/modal';
import withAncestry from 'ecommerce/hoc/withAncestry';
import uniq from 'utils/uniq';
import { getPagesByOptions, PAGE_OPTION, TREE_TYPE } from 'editor-utils/template';

import useEditorContext from '../../../context/editorContext';
import useEditorItemContext from '../../../context/editorItemContext';
import generateAncestryData from '../../../generator/generateAncestryData';
import DialogPanelBody from '../../components/dialogPanelBody';
import AddPageButton from '../../components/addPageButton';
import SelectedPersonsCounter from '../../components/selectedPersonsCounter';
import useAncestryFamilyTreeContext from '../../../context/ancestryFamilyTreeContext';
import PersonMultiSelectTabs from '../../people/personMultiSelectTabs';
import AncestryWebSocketSynchronizer from '../../../new/components/ancestryWebSocketSynchronizer';

const PersonalTimelinePanel = ({ ancestryAuth, onAddPages }) => {
  const modalContext = useModalContext();
  const editorContext = useEditorContext();
  const { item, template } = useEditorItemContext();
  const { projectPersons } = useAncestryFamilyTreeContext();

  const [selectedPersons, setSelectedPersons] = useState([]);

  const personsWithTimelinePages = useMemo(() => {
    const timelinePageIds = getPagesByOptions(template, [PAGE_OPTION.TIMELINE]).map(p => p.id);
    const timelinePages = item.pages.filter(page => timelinePageIds.indexOf(page.id) >= 0);

    const personIds = [];
    timelinePages.forEach(page => page.tags.forEach(personId => personIds.push(personId)));
    return uniq(personIds);
  }, [item, template]);

  const handleClick = async () => {
    const { treeId, treeName } = item.additionalInfo;

    modalContext.show(
      <AncestryWebSocketSynchronizer
        ancestryAuth={ancestryAuth}
        // there is not needed to add all the attributes, because I dont need to check sync status, because we fetch many persons
        progressLabel="We’re turning your data from Ancestry.com into a project preview."
        onSuccess={async ({ onProgress, close }) => {
          const { data, error } = await generateAncestryData(
            editorContext.accessToken,
            template,
            ancestryAuth,
            {
              type: "ancestry",
              arrayOfPersonIds: selectedPersons.map(person => [person.id]),
              optionIds: [PAGE_OPTION.TIMELINE],
              numberOfGenerations: 0,
              withoutCover: true,
              treeId,
              treeName,
              treeType: TREE_TYPE.SINGLE,
            },
            item.photos,
            ({ pending, total }) => {
              onProgress(((total - pending) / total) * 100);
            },
          );

          if (error) {
            editorContext.trackEvent('ancestry_generator_error');
            close();
            modalContext.confirm(<>{error}</>, {
              title: 'Error creating your project',
              closeable: false,
              confirmLabel: 'OK',
            });
            return;
          }

          editorContext.setPhotos(item.id, data.photos);
          const pages = data.pages.filter(page =>
            selectedPersons.find(person => page.tags.find(tag => tag === person.id)),
          );

          close();

          if (pages.length > 0) {
            onAddPages(pages);
          } else {
            modalContext.confirm(<>Sorry, we didn't find any data to selected person(s).</>, {
              title: 'No pages added',
              closeable: false,
              confirmLabel: 'OK',
            });
          }
        }}
      />,
      {
        title: 'Building your project',
        closeable: false,
      },
    );
  };

  const enabled = selectedPersons.length > 0;

  const sortedPersons = useMemo(() => {
    if (!projectPersons) {
      return null;
    }

    return [
      ...projectPersons
        .filter(p => personsWithTimelinePages.indexOf(p.id) < 0)
        .sort((a, b) => (a.name > b.name ? 1 : -1)),
      ...projectPersons
        .filter(p => personsWithTimelinePages.indexOf(p.id) >= 0)
        .sort((a, b) => (a.name > b.name ? 1 : -1)),
    ];
  }, [projectPersons, personsWithTimelinePages]);

  return (
    <DialogPanelBody
      actionButton={
        <>
          <SelectedPersonsCounter selectedPersons={selectedPersons} />
          <AddPageButton disabled={!enabled} onClick={handleClick} />
        </>
      }
    >
      <PersonMultiSelectTabs
        isLoading={projectPersons == null}
        onSelect={setSelectedPersons}
        people={sortedPersons}
        personRenderer={person => personsWithTimelinePages.indexOf(person.id) >= 0}
        treeId={item.additionalInfo.treeId}
      />
    </DialogPanelBody>
  );
};

PersonalTimelinePanel.propTypes = {
  onAddPages: PropTypes.func.isRequired,
};

export default withAncestry(PersonalTimelinePanel);
